import capitalize from 'lodash/capitalize';
import { ReactNode } from 'react';

import { Box, Flex, Text, VStack, useColorModeValue } from '@vimeo/bokeh';
import { ChevronDownSmall, CircleExclamationFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { useOrionSettings } from '@vimeo/orion';
import { type PaywallTrackingProps } from '@vimeo/picox';
import {
  DEFAULT_UPSELL_PRIVACIES,
  DEFAULT_PRIVACY_OPTIONS,
  VIDEO_PRIVACY_ICON_MAP,
  DEFAULT_PRIVACY_VALUES,
  getNewPrivacyCopy,
} from '@vimeo/privacy-management';
import {
  type SubscriptionPlans,
  useGetSubscriptionPlansData,
} from '@vimeo/subscription-api-plans-data-hook';
import { useViewer } from '@vimeo/viewer';

import { OtherPlansButton, PaywallCTAsWrapper, PurchaseButton } from '../components';

// `generic` renders the plan-level variant used when no single locked privacy
// is known (e.g. the cold-privacy banner, where videos across several privacies
// were forced to private). It keeps the same privacy-option preview but swaps
// the title/subtitle to plan-level copy.
type LockedPrivacyOption = 'unlisted' | 'password' | 'hide_from_vimeo' | 'generic';

interface PrivacyLimitPaywallProps {
  isUserEligibleForFreeTrial?: boolean;
  tier?: Partial<SubscriptionPlans['tier']>;
  privacyOption?: LockedPrivacyOption;
  paywallTracking: PaywallTrackingProps;
}

export function PrivacyLimitPaywall({
  tier = 'standard',
  privacyOption = 'unlisted',
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: PrivacyLimitPaywallProps) {
  const isGeneric = privacyOption === 'generic';
  const { settings } = useOrionSettings();
  const viewer = useViewer();
  const plans = useGetSubscriptionPlansData([tier], undefined, false);
  const planData = plans?.find((plan) => plan.tier === tier) ?? plans?.[0];
  const hasTrialPath = Boolean(planData?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = hasTrialPath && isUserEligibleForFreeTrial;
  const resolvedPlanName = planData?.name ?? (tier ? capitalize(`${tier}`) : '');
  const privacyImageUrl = useColorModeValue(
    'https://i.vimeocdn.com/custom_asset/967fe8edf9f28375f1d3988ed28585d9?region=us',
    'https://i.vimeocdn.com/custom_asset/fd161e8907c7bd204bcdd8ca6379312a?region=us',
  );
  const privacyOptions = DEFAULT_PRIVACY_OPTIONS.filter((option) =>
    DEFAULT_UPSELL_PRIVACIES.includes(option.privacy),
  )
    .map((option) =>
      settings.privacy_settings_new_copy
        ? getNewPrivacyCopy('xs', viewer?.teamUser?.teamName, viewer?.teamUser?.isWorkspace)(option)
        : option,
    )
    .map((option) => ({
      ...option,
      key:
        option.privacy === DEFAULT_PRIVACY_VALUES.HIDE_FROM_VIMEO
          ? 'hide_from_vimeo'
          : option.privacy,
      icon: settings.privacy_settings_new_copy
        ? (option.icon as React.JSX.Element)
        : VIDEO_PRIVACY_ICON_MAP[option.privacy].icon,
    }));

  // The generic variant has no single locked privacy, so fall back to the
  // `unlisted` ordering to keep the same preview stack as the design.
  const orderingPrivacyOption = isGeneric ? 'unlisted' : privacyOption;
  const lockedOption =
    privacyOptions.find((option) => option.key === orderingPrivacyOption) ?? privacyOptions[0];
  const backgroundOptions = privacyOptions.filter((option) => option.key !== lockedOption.key);
  const displayedOptions = [...backgroundOptions, lockedOption];

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      height={{ base: '100%', md: 'auto' }}
      maxHeight={{ base: 'none', md: 'calc(100vh - 120px)' }}
      position="relative"
    >
      <Flex
        direction="column"
        alignItems="center"
        width="100%"
        maxWidth="400px"
        maxHeight="100%"
        minHeight="0"
        gap="16px"
        flex="1 1 auto"
        pt={{ base: '24px', md: '0' }}
        pb="24px"
        mx={{ base: 'auto', md: '32px' }}
        overflowY="auto"
      >
        <Box
          width="192px"
          height="140px"
          minHeight="93px"
          flex="1 1 140px"
          backgroundImage={`url(${privacyImageUrl})`}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          marginY="8px"
        />

        <Flex direction="column" alignItems="center" gap="16px" width="100%" paddingX="16px">
          <Text variant={{ base: 'heading-xl', md: 'heading-2xl' }} textAlign="center">
            {isGeneric
              ? translate('Your plan doesn’t support all privacy settings')
              : translate({
                  singular: '{FEATURE_NAME} is not available',
                  replacements: { FEATURE_NAME: lockedOption.title },
                })}
          </Text>
          <Text variant="heading-sm" color="text-primary" textAlign="center" paddingX="16px">
            {isGeneric
              ? translate(
                  'Some privacy settings aren’t included in your current plan. All instances have been set to private.',
                )
              : translate({
                  singular:
                    'This privacy feature isn’t included in your current plan. Upgrade to {PLAN_NAME} to use {FEATURE_NAME}.',
                  replacements: { PLAN_NAME: resolvedPlanName, FEATURE_NAME: lockedOption.title },
                })}
          </Text>
        </Flex>

        <VStack width="100%" alignItems="stretch" position="relative" pt="24px" px="24px" gap="0">
          {displayedOptions.map((option, index) => (
            <PrivacyOptionRow
              key={option.key}
              icon={option.icon}
              title={option.title}
              description={option.description ?? ''}
              contentOpacity={index === 0 ? 0.2 : index === 1 ? 0.5 : undefined}
              rowOffset={index === 0 ? '0px' : index === 1 ? '16px' : '32px'}
            />
          ))}
          <CircleExclamationFilled
            color="upsell-primary"
            width="40px"
            height="40px"
            transform="rotate(15deg)"
            position="absolute"
            right="9px"
            top="-9px"
          />
        </VStack>
      </Flex>

      <PaywallCTAsWrapper>
        <PurchaseButton
          subscriptionTier={tier}
          isTrial={isTrial}
          planName={resolvedPlanName}
          paywallTracking={paywallTracking}
        />
        <OtherPlansButton paywallTracking={paywallTracking} />
      </PaywallCTAsWrapper>
    </Flex>
  );
}

function PrivacyOptionRow({
  icon,
  title,
  description,
  contentOpacity,
  rowOffset = '0px',
}: {
  icon: ReactNode;
  title: string;
  description: string;
  contentOpacity?: number;
  rowOffset?: string;
}) {
  return (
    <Flex
      border="1px solid"
      borderColor="input-stroke"
      borderRadius="12px"
      px="16px"
      py="8px"
      alignItems="center"
      gap="8px"
      ml={rowOffset}
      width="calc(100% - 32px)"
      bg="surface"
      mb="-16px"
      zIndex="1"
    >
      <Box opacity={contentOpacity}>{icon}</Box>
      <Flex direction="column" alignItems="start" flex="1" minWidth="0" opacity={contentOpacity}>
        <Text variant="heading-xs" color="text-primary" width="100%">
          {title}
        </Text>
        <Text variant="body-md" color="text-secondary" width="100%">
          {description}
        </Text>
      </Flex>
      <Box opacity={contentOpacity}>
        <ChevronDownSmall />
      </Box>
    </Flex>
  );
}
